/** Shared bits for every screen: loading and error states, status badges, formatting. */
import type { ReactNode } from "react";
import type { ApiError } from "../api/client.ts";
import { describeError } from "../api/hooks.ts";
import "./common.css";

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <p className="muted" role="status">{label}</p>;
}

export function ErrorNote({ error, onRetry }: { error: ApiError | Error; onRetry?: () => void }) {
  return (
    <div className="note note-bad" role="alert">
      <span>{describeError(error)}</span>
      {onRetry ? <button type="button" className="btn btn-quiet" onClick={onRetry}>Try again</button> : null}
    </div>
  );
}

export function Notice({ tone = "info", children }: { tone?: "info" | "warn" | "ok"; children: ReactNode }) {
  return <div className={`note note-${tone}`} role={tone === "warn" ? "alert" : "status"}>{children}</div>;
}

/**
 * Shown where a manager reads their reporting chain's work (AIC-124 follow-up): they can see it, not act on it. The
 * API refuses the actions anyway (403); the screens hide the controls so there is nothing to click.
 */
export const READ_ONLY_MANAGER = "Read-only for managers. Only the owning analyst or an admin can act on this.";
/** AIC-263: an admin's analyst preview is read-only too; the API refuses its actions (preview_read_only), so the screens
 * hide the controls and say why. */
export const READ_ONLY_PREVIEW = "Read-only in analyst preview. Exit preview to act on this.";
/** A view that is read-only although the caller may act elsewhere (Today showing another analyst's queue). */
export const READ_ONLY_VIEW = "Read-only in this view. Open the item in Review to act on it.";

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export const humanize = (s: string) => s.replace(/_/g, " ");

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" });
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export function formatUsd(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

/** A figure with its unit, as the spec and results carry them (usd_m, percent, count, text, month). */
export function formatValue(v: unknown, unit: string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") {
    if (unit === "usd_m") return `$${v.toLocaleString("en-US", { maximumFractionDigits: 3 })}M`;
    if (unit === "percent") return `${v.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
    if (unit === "month") {
      const m = new Date(2000, v - 1, 1).toLocaleString("en-US", { month: "long" });
      return Number.isInteger(v) && v >= 1 && v <= 12 ? m : String(v);
    }
    return v.toLocaleString("en-US");
  }
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

export function PageHeader({ title, sub, actions }: { title: string; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {sub ? <p className="muted">{sub}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}
